import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Stack,
  InputAdornment,
  Alert,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import apiService from '../../services/apiServices';

const forgotPasswordSchema = yup.object().shape({
  email: yup.string().email('Email i pavlefshëm').required('Email është i detyrueshëm'),
});

const ForgotPassword = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [directLink, setDirectLink] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const handleClose = () => {
    reset();
    setSuccess(false);
    setError('');
    setDirectLink('');
    onClose();
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await apiService().post('auth/forgot-password', {
        email: data.email,
      });

      if (response?.success) {
        setSuccess(true);
        if (response?.resetLink) {
          setDirectLink(response.resetLink);
        } else {
          // Mbyll automatikisht pas 3 sekondash
          setTimeout(() => {
            handleClose();
          }, 3000);
        }
      } else {
        setError(response?.error || 'Dërgimi i email-it dështoi.');
      }
    } catch (err) {
      setError(err?.message || 'Dërgimi i email-it dështoi.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div" fontWeight={600}>
          Rivendos fjalëkalimin
        </Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2}>
            {success ? (
              <>
                <Alert severity="success">
                  {directLink
                    ? 'Email nuk u dërgua (SMTP nuk është konfiguruar). Klikoni linkin më poshtë për të rivendosur fjalëkalimin:'
                    : 'Email u dërgua me sukses! Kontrollo inbox-in për instruksione.'}
                </Alert>
                {directLink && (
                  <Alert severity="info" sx={{ wordBreak: 'break-all' }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Link për rivendosje:</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      href={directLink}
                      sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      Hap faqen e rivendosjes
                    </Button>
                  </Alert>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Fut adresën e email-it të lidhur me llogarinë tënde. Do të pranosh
                  një link rivendosjeje me email — klikoje dhe vendos fjalëkalimin e ri.
                </Typography>

                {error && <Alert severity="error">{error}</Alert>}

                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} color="inherit">
            {success ? 'Mbyll' : 'Anulo'}
          </Button>
          {!success && (
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Duke dërguar...' : 'Dërgo email'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ForgotPassword;
